import Vue from 'vue';
import { Puzzle } from 'js/crossword/Puzzle';
import { Provider, PuzzleType } from 'js/crossword/types';
import { getSettingsData, getDefaultSettings, saveSettings, ISettingsData } from 'js/crossword/settingsData';
import { IUIData, PopupType, PrintType } from 'js/crossword/html/types';
import { getPuzzleFromURL } from 'js/crossword/fetchPuzzleData';
import { convertPuzzleDataToPuzzleState } from 'js/crossword/convertPuzzleDataToPuzzleState';
import { getFeedSlug } from 'js/crossword/sdk/getFeedSlug';
import { isPuzzleSolved, solvePuzzle, getFillRate } from 'js/crossword/puzzleDataHandlers/helpers';
import { trackGameFlow, GameFlowEvent } from 'js/tracking/track';
import { setCompleteTime } from "../completeTime";
import getLocalizationProvider from "js/framework/Localization/getLocalizationProvider";
import PopupLayer from "./PopupLayer";
import PrintPage from "./PrintPage";
import getGameTitle from "../helpers/getGameTitle";

declare const sdkHandler: any;
declare const sgSettings: any;

interface IData {
  puzzle: Puzzle;
  printPuzzle: Puzzle | null;
  ui: IUIData;
  sessionData: {
    hintsUsed: number;
    lastNotCompleteDate: number;
  };
  settings: ISettingsData;
}

export async function createGameScene(puzzle: Puzzle) {
  const root = document.createElement('div');
  document.body.appendChild(root);

  sdkHandler.trigger('levelStart', { level: 0 });

  const localization = await getLocalizationProvider(sgSettings.config.env.locale);

  const app = new Vue({
    provide() {
      return {
        [Provider.LOCALIZATION]: localization,
      };
    },
    data(): IData {
      return {
        puzzle,
        printPuzzle: null,
        ui: {
          popup: null,
          hiddenClues: false,
        },
        sessionData: {
          hintsUsed: 0,
          lastNotCompleteDate: 0,
        },
        settings: getSettingsData(),
      };
    },
    mounted() {
      const gameTitle = getGameTitle(this.puzzle.data.metaData.title);
      if (gameTitle !== "") {
        this.ui.popup = { type: PopupType.Welcome, data: { gameTitle } };
      }
    },
    watch: {
      'puzzle.data.cells': {
        handler() {
          const notCompleteCooldown = 60 * 1000;
          const canTriggerNonComplete = Date.now() - this.sessionData.lastNotCompleteDate > notCompleteCooldown;
          const { data } = this.puzzle;

          if (!data.completed && isPuzzleSolved(data)) {
            sdkHandler.trigger('levelFinish', { level: 0, score: 0, state: 'win' });
            data.completed = true;
            setCompleteTime(data.id, data.time);
            this.ui.popup = { type: PopupType.Complete, data: null };
            trackGameFlow(GameFlowEvent.CompletePuzzle);
          } else if (canTriggerNonComplete && getFillRate(data) === 1) {
            this.sessionData.lastNotCompleteDate = Date.now();
            this.ui.popup = { type: PopupType.NotComplete, data: null };
          }
        },
        deep: true,
      },
      'ui.popup': {
        handler(newVal) {
          this.puzzle.setPause(newVal !== null);
          this.ui.hiddenClues = newVal !== null;
        },
        immediate: true,
      },
      'settings': {
        handler: saveSettings,
        deep: true,
      },
    },
    methods: {
      setDefaultSettings() {
        Object.assign(this.settings, getDefaultSettings());
      },
      print(printType: PrintType) {
        const clone = this.puzzle.clone();

        if (printType === PrintType.Blank) {
          clone.clearPuzzle();
        } else if (printType === PrintType.Solution) {
          solvePuzzle(clone.data);
        }
        this.printPuzzle = clone;
        setTimeout(() => print(), 1);
      },
      async loadPuzzle(url) {
        const puzzleData = await getPuzzleFromURL(getFeedSlug(), url);
        this.puzzle = new Puzzle(convertPuzzleDataToPuzzleState(puzzleData));
        sdkHandler.trigger('levelStart', { level: 0 });
        this.sessionData.hintsUsed = 0;
        this.sessionData.lastNotCompleteDate = 0;
        trackGameFlow(GameFlowEvent.StartPuzzle);
      },
    },
    render() {
      return (
        <div>
          <input type='text' id='mobileTextInput' autocomplete='off' spellcheck='false' autocorrect='off'/>
          <div id='gameScene' ref='gameScene'>
            <div id='gameScene__topBar'>
              <top-menu-bar
                settings={this.settings}
                sessionData={this.sessionData}
                ui={this.ui}
                puzzle={this.puzzle}
                // @ts-ignore
                onZoomIn={() => this.$refs.gameArea.zoomBoardIn()}
                // @ts-ignore
                onZoomOut={() => this.$refs.gameArea.zoomBoardOut()}
                onPrintPuzzle={this.print}
              />
            </div>

            { this.puzzle.data.type === PuzzleType.Arrow
              ? <puzzle-game-area-arrow
                  puzzle={this.puzzle}
                  settings={this.settings}
                  ui={this.ui}
                  gameScene={this.$refs.gameScene}
                  ref='gameArea'
                />
              : <puzzle-game-area-regular
                  puzzle={this.puzzle}
                  settings={this.settings}
                  ui={this.ui}
                  gameScene={this.$refs.gameScene}
                  ref='gameArea'
                />
            }
            
            <PopupLayer
              puzzle={this.puzzle}
              settings={this.settings}
              ui={this.ui}
              onPrintPuzzle={this.print}
              onLoadPuzzle={this.loadPuzzle}
            />
          </div>
          { this.printPuzzle ? <PrintPage puzzle={this.printPuzzle}/> : null }
        </div>
      );
    },
  });

  app.$mount(root);
}
